<template>
    <div id="settings-page-v2">
        <AppBreadCrumb :paths="[{ title: $t('message.settings') }]" :show-trial-remain-day="true" />
        <div class="settings-page-v2-wrapper">
            <SidebarChild
                :items="SettingsMenuTypes"
                :selected-item="activeTab"
                @selected="selectedTab"
                :focusActive="true"
                :defaultOpen="true"
            />
            <div class="page-content" v-if="settingInitialized">
                <UpgradeNeededPageOverlay
                    v-if="upgrade.enabled && upgrade.type === 'settings-overlay'"
                    :plan="upgrade.plan"
                    :feature="upgrade.feature"
                    :name="upgrade.name"
                />
                <Company v-else-if="activeTab === 'company'" :update-settings="updateSingleSettings" />
                <Features v-else-if="activeTab === 'features'" :update-settings="updateSingleSettings" />
                <Language v-else-if="activeTab === 'language'" :update-settings="updateSingleSettings" />
                <ChatTimingSettings
                    v-else-if="activeTab === 'timings-settings'"
                    :update-settings="updateSingleSettings"
                />
                <Availability v-else-if="activeTab === 'availability'" :update-settings="updateSingleSettings" />
                <WebSiteIntegration
                    v-else-if="activeTab === 'website-integration'"
                    :update-settings="updateSingleSettings"
                    class="website-integrations"
                />
                <ApiAccess v-else-if="activeTab === 'developer'" :update-settings="updateSingleSettings" />
                <ProfileFields v-else-if="activeTab === 'visitor-attribute'" />
                <AutoChatAssign v-else-if="activeTab === 'auto-assign'" :update-settings="updateSingleSettings" />
                <Wallet v-else-if="activeTab === 'wallet'" :update-settings="updateSingleSettings" />
                <NotificationSettings
                    v-else-if="activeTab === 'notifications'"
                    :update-settings="updateSingleSettings"
                />
                <AuditLogs v-else-if="activeTab === 'audit-logs'" :update-settings="updateSingleSettings" />
                <AccessControl v-else-if="activeTab === 'access-control'" />
                <Users v-else-if="activeTab === 'company-users'" />
                <Roles
                    v-else-if="activeTab === 'roles'"
                    :is-edit="isRoleEdit"
                    :role-id="editRoleId"
                    @route-change="handleRoleRouteChange"
                />
                <Subjects v-else-if="activeTab === 'departments'" />
                <WelcomeMessages v-else-if="activeTab === 'system-messages'" />
                <Templates v-else-if="activeTab === 'templates'" />
                <Tags v-else-if="activeTab === 'tags'" />
                <BannedWords v-else-if="activeTab === 'forbidden-inputs'" />
                <BannedVisitors v-else-if="activeTab === 'banned-visitors'" />
                <Billing v-else-if="activeTab === 'billing'" />
            </div>
        </div>
    </div>
</template>

<script>
import "./settings.scss";
import AppBreadCrumb from "../../../components/common/AppBreadCrumb.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import * as _ from "lodash";
import log2 from "../../../utils/log2";
import { sendSocketMessage } from "../../../utils/socketStream";
import Company from "./pages/company/Company.vue";
import Features from "./pages/general/Features.vue";
import Language from "./pages/general/Language.vue";
import ChatTimingSettings from "./pages/chat/ChatTimingSettings.vue";
import ApiAccess from "./pages/ApiAccess.vue";
import WebSiteIntegration from "./pages/WebSiteIntegration.vue";
import Availability from "./pages/chat/Availability.vue";
import SettingsMenuTypes from "./SettingsMenuTypes";
import ProfileFields from "./pages/company/ProfileFields.vue";
import AutoChatAssign from "./pages/chat/AutoChatAssign.vue";
import { PERMISSION_TYPES } from "../../../language/permissionTypes";
import systemPubSub from "../../../utils/systemPubSub";
import Wallet from "./pages/general/Wallet.vue";

import { filter } from "rxjs/operators";
import NotificationSettings from "./pages/notification-settings/NotificationSettings.vue";
import AuditLogs from "./pages/company/audit-log/AuditLogs.vue";
import SidebarChild from "../../../components/Sidebar/SidebarChild.vue";
import AccessControl from "./pages/company/AccessControl.vue";
import UpgradeNeededPageOverlay from "../../../components/billing/UpgradeNeededPageOverlay.vue";
import Users from "./pages/company/Users.vue";
import Roles from "./pages/company/roles/Roles.vue";
import Subjects from "./pages/company/Subjects.vue";
import WelcomeMessages from "./pages/chat/WelcomeMessages.vue";
import Templates from "./pages/chat/Templates.vue";
import Tags from "./pages/company/Tags.vue";
import BannedWords from "./pages/chat/BannedWords.vue";
import BannedVisitors from "./pages/chat/BannedVisitors.vue";
import Billing from "../Billing/Billing.vue";

const log = log2("Settings");
export default {
    name: "settings",
    components: {
        BannedVisitors,
        BannedWords,
        Tags,
        Templates,
        WelcomeMessages,
        Subjects,
        Roles,
        Users,
        UpgradeNeededPageOverlay,
        AccessControl,
        AuditLogs,
        NotificationSettings,
        AutoChatAssign,
        AppBreadCrumb,
        Company,
        Features,
        Language,
        ChatTimingSettings,
        ApiAccess,
        WebSiteIntegration,
        Availability,
        ProfileFields,
        Wallet,
        SidebarChild,
        Billing,
    },
    props: {
        tab: {
            type: String,
            default: null,
        },
        initialIsRoleEdit: {
            type: Boolean,
            default: false,
        },
        initialRoleId: {
            type: String,
            default: null,
        },
    },
    computed: {
        ...mapState({
            settings: (state) => state.setting.settings,
            settingInitialized: (state) => state.setting.initialized,
            site: (state) => state.site.site,
            languages: (state) => state.language.languages,
            defaultLang: (state) => state.setting.settings.language.clientDefault,
            isIdeaSoft: (state) => _.get(state.site.site, "partner.name") === "ideasoft",
            isAdmin: (state) => state.auth.adminUser != null,
            isProPlanUser: (state) => _.get(state.setting.settings, "plan.selectedPlan") === "pro",
            systemLanguages: (state) => state.setting.settings.systemLanguages,
            user: (state) => state.auth.user,
            adminUser: (state) => state.auth.adminUser,
            upgrade: (state) => state.app.upgrade,
        }),
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
            checkPermission: "auth/checkPermission",
        }),
    },
    data() {
        return {
            SettingsMenuTypes: SettingsMenuTypes(this),
            activeTab: this.tab || this.$route.path.replace("/settings/", "") || "notifications",
            editMode: false,
            isV2Theme: false,
            isVersionV2: false,
            systemPubSubs: [],
            handleSave: _.debounce((newSettings) => {
                const isValid = Date.now() - (this.mountedTime || Date.now()) > 2000; // defaulttan gerçeğe geçerken bir kere sunucuya gitmesini engellemek için alternatif başka çözüm var sa yapalım
                if (isValid) {
                    this.submitClicked = true;
                    const self = this;
                    this.$validator.validateAll().then((response) => {
                        if (response) {
                            this.publishSettings(newSettings);
                        } else {
                            self.$message.error({
                                content: self.$t("message.settingFormWrong"),
                                duration: 2,
                                key: "saved",
                            });
                        }
                    });
                }
            }, 750).bind(this),
            isRoleEdit: this.initialIsRoleEdit,
            editRoleId: this.initialRoleId,
        };
    },

    methods: {
        ...mapActions({
            getSettings: "setting/getSettings",
            updateSettings: "setting/updateSettings",
            setSettingValue: "setting/setSettingValue",
        }),
        selectedTab({ key }) {
            this.activeTab = key;

            if (key === "roles") {
                this.isRoleEdit = false;
                this.editRoleId = null;
                this.$router.push(`/settings/roles`);
            } else {
                if (this.activeTab !== "roles") {
                    this.isRoleEdit = false;
                    this.editRoleId = null;
                }
                this.$router.push(`/settings/${this.activeTab}`);
            }
        },
        publishSettings(settings, updatingFields) {
            this.updateSettings(settings)
                .then(() => {
                    sendSocketMessage("user settings-updated", {
                        siteId: this.site._id,
                    });
                    this.$message.success({
                        content: this.$t("message.settingsSaved"),
                        duration: 2,
                        key: "saved",
                    });
                })
                .catch((error) => {
                    this.$message.error({
                        content: this.$t("message.settingsCannotSaved"),
                        duration: 2,
                        key: "saveFailed",
                    });
                })
                .finally(() => {
                    this.submitClicked = false;
                });
        },
        updateSingleSettings(key, value, immediately = false) {
            log("updateSingleSettings called in settings.vue", { key, value, immediately });
            if (this.checkPermission(PERMISSION_TYPES["setting.update"].tr.name)) {
                this.setValue(key, value, immediately);
            } else {
                this.$message.error({
                    content: this.$t("message.noAuthorization"),
                    duration: 2,
                    key: "saved",
                });
            }
        },
        async setValue(key, value, immediately) {
            log("setValue called in settings.vue", { key, value, immediately });
            if (!this.settingInitialized || this.isBusy) {
                log.warn("setValue", "setting not initialized", { key, value });
                return;
            }
            const payload = {
                setting: key,
                value,
            };
            const newSettings = await this.setSettingValue(payload);
            if (immediately) {
                this.publishSettings(newSettings, payload);
            } else {
                this.handleSave(newSettings, payload);
            }
        },
        handleRoleRouteChange(isEdit, roleId) {
            this.isRoleEdit = isEdit;
            this.editRoleId = roleId;
            this.activeTab = "roles";

            if (isEdit) {
                this.$router.push(`/settings/roles/${roleId}/edit`);
            } else {
                this.$router.push(`/settings/roles`);
            }
        },
    },
    mounted() {
        this.getSettings().then(() => {
            this.isBusy = false;
        });
        const settingsPubSub = systemPubSub.stream
            .pipe(filter((x) => x.type === "settings_page_changed"))
            .subscribe((x) => {
                this.activeTab = x.payload.name;
                if (this.activeTab !== "roles") {
                    this.isRoleEdit = false;
                    this.editRoleId = null;
                } else if (this.activeTab === "roles" && !this.$route.params.id) {
                    this.isRoleEdit = false;
                    this.editRoleId = null;
                }
            });
        this.systemPubSubs.push(settingsPubSub);
        this.mountedTime = Date.now();
        this.SettingsMenuTypes = SettingsMenuTypes(this);
    },
    created() {
        const selected = SettingsMenuTypes(this).find((x) => x.key === this.activeTab);
        if (!selected?.enabled) this.activeTab = "notifications";

        if (this.activeTab === "roles" && this.$route.params.id) {
            this.isRoleEdit = true;
            this.editRoleId = this.$route.params.id;
        } else {
            if (this.initialIsRoleEdit !== undefined) {
                this.isRoleEdit = this.initialIsRoleEdit;
            }

            if (this.initialRoleId !== undefined) {
                this.editRoleId = this.initialRoleId;
            }
        }
    },
    watch: {
        tab(newValue) {
            if (newValue) {
                this.activeTab = newValue;
                if (newValue !== "roles") {
                    this.isRoleEdit = false;
                    this.editRoleId = null;
                }
            }
        },
        $route(to, from) {
            if (this.activeTab === "roles") {
                if (to.params.id) {
                    this.isRoleEdit = true;
                    this.editRoleId = to.params.id;
                } else {
                    this.isRoleEdit = false;
                    this.editRoleId = null;
                }
            }
        },
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) {
            this.$validator.localize(this.$i18n.locale);
            this.$forceUpdate();
        }
    },
    beforeDestroy() {
        const supsis = window.supsis;
        if (!!supsis) supsis("destroy");
        this.systemPubSubs.map((sub) => sub.unsubscribe());
        this.isPreviewActive = false;
    },
};
</script>

<style scoped>
.dark-mode .settings-page-v2-wrapper {
    background-color: #343a40 !important;
}
</style>
